import * as XLSX from "xlsx";
import { calcRequiredFTE, calcEffectiveHours, calcLocalization } from "./calculations";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Apply header style helper
function styleHeader(ws, range) {
  const ref = XLSX.utils.decode_range(range);
  for (let C = ref.s.c; C <= ref.e.c; C++) {
    const addr = XLSX.utils.encode_cell({ r: ref.s.r, c: C });
    if (!ws[addr]) continue;
    ws[addr].s = {
      font: { bold: true, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "4B5E4D" } },
      alignment: { horizontal: "center" },
    };
  }
}

function addSheetTitle(ws, title, colCount) {
  // Title row
  ws["A1"] = { v: title, t: "s", s: { font: { bold: true, sz: 13, color: { rgb: "1C241D" } } } };
  ws["A2"] = { v: `Generated by TheKaizenCabinet  ·  ${new Date().toLocaleString()}`, t: "s", s: { font: { italic: true, sz: 9, color: { rgb: "7C9A7E" } } } };
  ws["!merges"] = ws["!merges"] || [];
  ws["!merges"].push({ s: { r: 0, c: 0 }, e: { r: 0, c: colCount - 1 } });
  ws["!merges"].push({ s: { r: 1, c: 0 }, e: { r: 1, c: colCount - 1 } });
}

export function exportWorkbookExcel({ project, company, effectiveHours }) {
  if (!project) return;

  const wb = XLSX.utils.book_new();
  const org = project.setup?.org_profile || {};
  const vc = project.setup?.volume_capacity || {};
  const qw = project.setup?.quality_waste || {};
  const eh = project.setup?.effective_hours || {};
  const cal = project.calibration || {};
  const stages = project.stages || [];
  const demand = project.demand || {};
  const diag = project.diagnostic;
  const eff = effectiveHours || calcEffectiveHours(eh);
  const totalVolume = vc.total_monthly_volume || 0;
  const currency = org.currency || "USD";

  // ── Sheet 1: Summary ────────────────────────────────────────────────
  const summaryData = [
    ["TheKaizenCabinet Workforce Optimisation Report"],
    [`Generated: ${new Date().toLocaleString()}`],
    [],
    ["ORGANISATION PROFILE"],
    ["Field", "Value"],
    ["Organisation Name", org.company_name || company?.name || "—"],
    ["Department / BU", org.department || company?.department || "—"],
    ["Country / Region", org.country || company?.country || "—"],
    ["Business Domain", org.domain || company?.domain || "—"],
    ["Currency", currency],
    ["Localisation Target %", org.localization_target || 20],
    ["Tolerance Band ±pp", org.tolerance_band || 5],
    [],
    ["VOLUME & CAPACITY"],
    ["Field", "Value"],
    ["Total Monthly Volume", totalVolume],
    ["Working Days / Month", vc.working_days_per_month || 22],
    ["Cost per FTE / Month", vc.cost_per_fte || 0],
    ["Target SLA %", vc.target_sla || 95],
    [],
    ["QUALITY & WASTE"],
    ["Field", "Value"],
    ["Rework / Error Rate %", qw.rework_rate || 0],
    ["NVA Time %", qw.nva_time || 0],
    ["Absenteeism Rate %", qw.absenteeism_rate || 0],
    ["Annual Attrition Rate %", qw.attrition_rate || 0],
    ["Onboarding Duration (weeks)", qw.onboarding_weeks || 4],
  ];

  if (diag) {
    const stats = diag.summary_stats || {};
    const wi = diag.workforce_intel || {};
    summaryData.push(
      [],
      ["DIAGNOSTIC SUMMARY"],
      ["Field", "Value"],
      ["Total FTE (Current)", stats.total_current_fte || 0],
      ["Required FTE (Model)", stats.total_required_fte || 0],
      ["Headcount Delta", stats.headcount_delta || 0],
      ["Monthly Cost", `${currency} ${(stats.monthly_cost || 0).toLocaleString()}`],
      ["Potential Saving/Month", `${currency} ${(stats.potential_saving || 0).toLocaleString()}`],
      ["Local Nationals", `${wi.local_count || 0} (${wi.local_pct || 0}%)`],
      ["Expatriates", `${wi.expat_count || 0}`],
      ["Localisation Status", (wi.localization_status || "—").replace("_", " ")],
    );
  }

  const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
  wsSummary["!cols"] = [{ wch: 32 }, { wch: 40 }];
  XLSX.utils.book_append_sheet(wb, wsSummary, "Summary");

  // ── Sheet 2: Process Stages ─────────────────────────────────────────
  const stageHeaders = [
    "Stage Name", "Type", "Priority", "Yield %", "AHT (min)",
    "Local Count", "Expat Count", "Total Team",
    "Utilisation %", "Productivity %",
    "Required FTE", "Current FTE", "Delta",
    "Local % (FTE Count)", "Local % (Effort-Weighted)", "Localisation Flag",
  ];

  const stageRows = stages.map((s) => {
    const reqFTE = calcRequiredFTE(s, totalVolume, eff);
    const currFTE = (s.local_count || 0) + (s.expat_count || 0);
    const loc = calcLocalization(s, org, eff);
    return [
      s.stage_name || "",
      s.stage_type || "",
      s.priority || "",
      s.yield_pct || 100,
      s.aht || 10,
      s.local_count || 0,
      s.expat_count || 0,
      currFTE,
      s.utilization || 75,
      s.productivity || 80,
      Math.round(reqFTE * 10) / 10,
      currFTE,
      Math.round((currFTE - reqFTE) * 10) / 10,
      parseFloat(loc.fteCountBasis) || 0,
      parseFloat(loc.effortWeighted) || 0,
      loc.flag === "ok" ? "On Target" : loc.flag === "over" ? "Above Target" : "Below Target",
    ];
  });

  // Totals row
  const totalLocal = stages.reduce((a, s) => a + (s.local_count || 0), 0);
  const totalExpat = stages.reduce((a, s) => a + (s.expat_count || 0), 0);
  const totalTeam = totalLocal + totalExpat;
  const totalReqFTE = stageRows.reduce((a, r) => a + (r[10] || 0), 0);
  const totalsRow = ["TOTAL", "", "", "", "", totalLocal, totalExpat, totalTeam, "", "", Math.round(totalReqFTE * 10) / 10, totalTeam, Math.round((totalTeam - totalReqFTE) * 10) / 10, totalTeam > 0 ? Math.round(totalLocal / totalTeam * 1000) / 10 : 0, "", ""];

  const wsStages = XLSX.utils.aoa_to_sheet([stageHeaders, ...stageRows, totalsRow]);
  wsStages["!cols"] = [{ wch: 28 }, { wch: 16 }, { wch: 10 }, { wch: 8 }, { wch: 8 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 12 }, { wch: 12 }, { wch: 10 }, { wch: 8 }, { wch: 16 }, { wch: 20 }, { wch: 16 }];
  XLSX.utils.book_append_sheet(wb, wsStages, "Process Stages");

  // ── Sheet 3: Effective Hours ─────────────────────────────────────────
  const effHeaders = ["Parameter", "Value"];
  const effRows = [
    ["Gross Working Days / Year", eh.gross_working_days || 260],
    ["Public Holidays", eh.public_holidays || 10],
    ["Annual Leave — Expat (days)", eh.annual_leave_expat || 21],
    ["Annual Leave — Local (days)", eh.annual_leave_local || 30],
    ["Sick Leave Days", eh.sick_leave_days || 5],
    ["Other Absence Days", eh.other_absence_days || 3],
    ["Shift Hours — Expat", eh.shift_hours_expat || 8],
    ["Shift Hours — Local", eh.shift_hours_local || 5],
    ["Prayer / Break Hours", eh.prayer_break || 0.5],
    [],
    ["COMPUTED OUTPUTS", ""],
    ["Net Working Days — Expat", Math.round(eff.netDaysExpat * 10) / 10],
    ["Net Working Days — Local", Math.round(eff.netDaysLocal * 10) / 10],
    ["Monthly Hours — Expat", Math.round(eff.monthlyHrsExpat * 10) / 10],
    ["Monthly Hours — Local", Math.round(eff.monthlyHrsLocal * 10) / 10],
    ["Annual Hours — Expat", Math.round(eff.annualHrsExpat * 10) / 10],
    ["Annual Hours — Local", Math.round(eff.annualHrsLocal * 10) / 10],
    ["Conversion Factor (1 local = X expat)", eff.conversionFactor],
    ["Capacity Gap %", `${eff.capacityGapPct}%`],
  ];
  const wsEff = XLSX.utils.aoa_to_sheet([effHeaders, ...effRows]);
  wsEff["!cols"] = [{ wch: 36 }, { wch: 18 }];
  XLSX.utils.book_append_sheet(wb, wsEff, "Effective Hours");

  // ── Sheet 4: Calibration ─────────────────────────────────────────────
  const calHeaders = ["Stage Name", "Global Util %", "Global Prod %", "Override Util %", "Override Prod %"];
  const calRows = stages.map((s, i) => {
    const stageId = s.id || i;
    const ov = cal.stage_overrides?.[stageId] || {};
    return [
      s.stage_name || "",
      cal.global_utilization || 75,
      cal.global_productivity || 80,
      ov.utilization ?? "—",
      ov.productivity ?? "—",
    ];
  });
  const wsCal = XLSX.utils.aoa_to_sheet([
    ["Global Utilisation %", cal.global_utilization || 75],
    ["Global Productivity %", cal.global_productivity || 80],
    [],
    calHeaders,
    ...calRows,
  ]);
  wsCal["!cols"] = [{ wch: 28 }, { wch: 14 }, { wch: 14 }, { wch: 16 }, { wch: 16 }];
  XLSX.utils.book_append_sheet(wb, wsCal, "Calibration");

  // ── Sheet 5: Demand Planner ──────────────────────────────────────────
  const monthlyData = demand.monthly_data || [];
  const demandHeaders = ["Month", "Volume", "Local Util %", "Local Prod %", "Expat Util %", "Expat Prod %", "Total Req FTE"];
  const demandRows = MONTHS.map((m, i) => {
    const md = monthlyData[i] || {};
    const vol = md.volume ?? totalVolume;
    let fteTot = 0;
    stages.forEach((s) => {
      const lc = s.local_count || 0;
      const ec = s.expat_count || 0;
      const tc = lc + ec;
      if (!tc) return;
      const sv = vol * ((s.yield_pct || 100) / 100);
      const blendedProd = (lc * (md.local_prod ?? 80) + ec * (md.expat_prod ?? 80)) / tc;
      const blendedHrs = (lc * eff.monthlyHrsLocal + ec * eff.monthlyHrsExpat) / tc;
      const blendedUtil = (lc * (md.local_util ?? 75) + ec * (md.expat_util ?? 75)) / tc;
      const tmn = sv * (s.aht || 10) / Math.max(blendedProd / 100, 0.01);
      const amp = blendedHrs * 60 * Math.max(blendedUtil / 100, 0.01);
      fteTot += amp > 0 ? tmn / amp : 0;
    });
    return [m, vol, md.local_util ?? 75, md.local_prod ?? 80, md.expat_util ?? 75, md.expat_prod ?? 80, Math.round(fteTot * 10) / 10];
  });
  const wsDemand = XLSX.utils.aoa_to_sheet([
    ["Next Year Volume Delta %", demand.next_year_delta || 10],
    [],
    demandHeaders,
    ...demandRows,
  ]);
  wsDemand["!cols"] = [{ wch: 8 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 14 }];
  XLSX.utils.book_append_sheet(wb, wsDemand, "Demand Planner");

  // ── Sheet 6: Diagnostic ──────────────────────────────────────────────
  if (diag) {
    const diagData = [
      ["LEAN DIAGNOSTIC REPORT"],
      [`Generated: ${diag.generated_at ? new Date(diag.generated_at).toLocaleString() : "—"}`],
      [],
      ["SUMMARY STATS"],
      ["Metric", "Value"],
      ["Total FTE", diag.summary_stats?.total_current_fte || 0],
      ["Required FTE", diag.summary_stats?.total_required_fte || 0],
      ["Headcount Delta", diag.summary_stats?.headcount_delta || 0],
      ["Monthly Cost", diag.summary_stats?.monthly_cost || 0],
      ["Potential Saving/Month", diag.summary_stats?.potential_saving || 0],
      [],
      ["WASTE DIAGNOSTIC ALERTS"],
      ["Alert Type", "Value", "Severity", "Message"],
      ...(diag.alerts || []).map((a) => [a.type, a.value, a.severity, a.message]),
      [],
      ["LEAN LEVERS"],
      ["Title", "Priority", "Description", "Tags"],
      ...(diag.lean_levers || []).map((l) => [l.title, l.priority, l.body, (l.tags || []).join(", ")]),
      [],
      ["LEAN ROADMAP"],
      ["Phase", "Title", "Timeline", "Description", "Key Actions"],
      ...(diag.roadmap || []).map((p) => [p.phase, p.title, p.months, p.description, (p.actions || []).join("; ")]),
    ];
    const wsDiag = XLSX.utils.aoa_to_sheet(diagData);
    wsDiag["!cols"] = [{ wch: 10 }, { wch: 40 }, { wch: 12 }, { wch: 60 }, { wch: 60 }];
    XLSX.utils.book_append_sheet(wb, wsDiag, "Diagnostic");
  }

  // Save
  const filename = `SAGE_${(org.company_name || company?.name || "Report").replace(/\s+/g, "_")}_${project.name?.replace(/\s+/g, "_") || "Export"}_${new Date().toISOString().slice(0, 10)}.xlsx`;
  XLSX.writeFile(wb, filename);
}
